import { CompanyCategories, Request, RequestCategory, RequestHistory, RequestSubcategory } from '@centecomm/portal-service';
import { createSelector } from 'reselect';
import { LoadStatus, State, StateSelector } from '../../Types';
import { RequestState } from './Types';

const requestState = ({ requests }: State) => requests;

/* Requests */
const requestsSlice = createSelector(requestState, ({ requests }: RequestState) => requests);

export const requestsLoadStatus = createSelector(requestsSlice, (requests) => requests.loadStatus);

export const requests = createSelector(requestsSlice, (stateRequests) =>
    Object.values(stateRequests).filter((r: LoadStatus | Request) => typeof r === 'object'),
);

export const request = (requestId: string | number): StateSelector<Request> =>
    createSelector(requestsSlice, (stateRequests) => stateRequests[requestId]);

export const requestDescription = (requestId: string | number): StateSelector<string> =>
    createSelector(requestsSlice, (stateRequests) =>
        stateRequests[requestId]?.description ? stateRequests[requestId].description : '',
    );

export const requestIsLoaded = (requestId: string | number): StateSelector<boolean> =>
    createSelector(requestsSlice, (stateRequests) => Object.hasOwnProperty.call(stateRequests, requestId));

/* Categories */
const categoriesSlice = createSelector(requestState, ({ categories }: RequestState) => categories);

export const categoriesLoadStatus = createSelector(categoriesSlice, (categories) => categories.loadStatus);

export const companyCategories = createSelector(categoriesSlice, ({ loadStatus, ...stateCategories }) =>
    Object.keys(stateCategories).map((key) => stateCategories[key]),
);

export const categoriesByCompany = (companyId: number): StateSelector<RequestCategory[]> =>
    createSelector(
        companyCategories,
        (list: CompanyCategories[]) =>
            list.find((c: CompanyCategories) => c.companyId === companyId)?.categories || ([] as RequestCategory[]),
    );

export const category = (categoryId: number, companyId: number): StateSelector<RequestCategory | null> =>
    createSelector(
        categoriesByCompany(companyId),
        (categories: RequestCategory[]) => categories.find((c: RequestCategory) => c.id === categoryId) || null,
    );

export const categoriesList = (companyId: number): StateSelector<RequestCategory[]> =>
    createSelector(categoriesByCompany(companyId), (list: RequestCategory[]) => list || ([] as RequestCategory[]));

export const categories = (companyId: number): StateSelector<Record<RequestCategory['id'], RequestCategory>> =>
    createSelector(categoriesList(companyId), (list: RequestCategory[]) =>
        list.reduce((acc, c) => ({ ...acc, [c.id]: c }), {}),
    );

export const subcategories = (categoryId: number, companyId: number): StateSelector<RequestSubcategory[]> =>
    createSelector(category(categoryId, companyId), (stateCategory: RequestCategory) => stateCategory?.subCategories || []);

export const subcategory = (
    categoryId: number,
    subcategoryId: number,
    companyId: number,
): StateSelector<RequestSubcategory | null> =>
    createSelector(
        subcategories(categoryId, companyId),
        (stateSubcategories: RequestSubcategory[]) =>
            stateSubcategories.find((s: RequestSubcategory) => s.id === subcategoryId) || null,
    );

/* Notes */
const notesSlice = createSelector(requestState, ({ requestNotes }: RequestState) => requestNotes);

export const notes = (requestId: number): StateSelector<string[]> =>
    createSelector(notesSlice, (stateNotes) => (Object.hasOwnProperty.call(stateNotes, requestId) ? notes[requestId] : []));

/* History */
const historySlice = createSelector(requestState, ({ requestHistory }: RequestState) => requestHistory);

export const history = (requestId: number): StateSelector<RequestHistory[]> =>
    createSelector(historySlice, (stateHistory) =>
        Object.hasOwnProperty.call(stateHistory, requestId) ? stateHistory[requestId] : [],
    );

export const historyLoadedForRequest = (requestId: number): StateSelector<boolean> =>
    createSelector(historySlice, (stateHistory) => Object.hasOwnProperty.call(stateHistory, requestId));

/* Pending Attachments */
const pendingAttachmentSlice = createSelector(requestState, ({ pendingAttachments }: RequestState) => pendingAttachments);

export const pendingAttachments = (requestId: number): StateSelector<File[]> =>
    createSelector(pendingAttachmentSlice, (pending) =>
        Object.hasOwnProperty.call(pending, requestId) ? pending[requestId] : [],
    );

export const hasPendingAttachments = (requestId: number): StateSelector<boolean> =>
    createSelector(pendingAttachmentSlice, (pending) => Object.hasOwnProperty.call(pending, requestId));

/* Filters */
const categoryFiltersSlice = createSelector(requestState, ({ requestFilters }: RequestState) => requestFilters.categories);
const priorityFiltersSlice = createSelector(requestState, ({ requestFilters }: RequestState) => requestFilters.priorities);

export const categoryFiltersLoadStatus = createSelector(
    categoryFiltersSlice,
    (categoryFilters) => categoryFilters.loadStatus,
);
export const priorityFiltersLoadStatus = createSelector(
    priorityFiltersSlice,
    (priorityFilters) => priorityFilters.loadStatus,
);

export const categoryFilters = createSelector(categoryFiltersSlice, ({ loadStatus, ...storedCategoryFilters }) =>
    Object.keys(storedCategoryFilters).map((key) => storedCategoryFilters[key]),
);
export const priorityFilters = createSelector(priorityFiltersSlice, ({ loadStatus, ...storedPriorityFilters }) =>
    Object.keys(storedPriorityFilters).reduce((acc, key) => {
        const priority = storedPriorityFilters[key];
        return { ...acc, [key]: priority };
    }, {}),
);
